import { useController, useFormContext } from "react-hook-form";
import { format, isValid, parseISO } from "date-fns";
import { ChipSelector, Text } from "../../components";
import { useApi } from "../../hooks";
import { SVGs } from "../../assets";
import { useCallback } from "react";
import { APIs } from "../../apis";
import * as React from "react";

type Option = { value: string; label: string };

export type AvailableSlotsProps = {
  t: (key: string) => string;
  productId: number | null;
  partnerProductLocationId: number | null;
  timeZone?: string | null;
  initialSlots?: string[];
};

function useViewModel(props: AvailableSlotsProps) {
  const { fetch: fetchTimeSlots, loading } = useApi();
  const form = useFormContext();
  const {
    field: { value, onChange },
  } = useController({
    control: form.control,
    name: "serviceTime",
    rules: {
      required: {
        value: true,
        message: props.t("booking.validation.timeSlotRequired"),
      },
    },
  });
  const [slots, setSlots] = React.useState<string[]>([]);
  const bookingDate = form.watch("serviceDate");
  const errorMessage = (form.formState.errors.serviceTime?.message ||
    (!slots?.length ? props.t("booking.noTimeSlotsForDate") : "")) as
    | string
    | undefined;

  const options = React.useMemo<Option[]>(
    () =>
      slots.map((iso) => {
        const d = parseISO(iso);
        return { value: iso, label: isValid(d) ? format(d, "HH:mm") : iso };
      }),
    [slots],
  );

  const run = useCallback(async () => {
    try {
      const { slots = [] } = await fetchTimeSlots(() =>
        APIs.order.timeSlots({
          partnerProductLocationId: props.partnerProductLocationId!,
          partnerProductId: props.productId!,
          bookingDate: bookingDate!,
        }),
      );
      setSlots(slots);
      onChange(slots[0]);
    } catch {
      onChange(undefined);
    }
  }, [
    bookingDate,
    fetchTimeSlots,
    props.productId,
    onChange,
    props.partnerProductLocationId,
  ]);

  React.useEffect(() => {
    run();
  }, [run]);

  return {
    errorMessage,
    value,
    loading,
    options,
    onChange,
    bookingDate,
  };
}

export const AvailableSlots = React.memo(function AvailableSlots({
  ...props
}: AvailableSlotsProps) {
  const { errorMessage, options, loading, value, onChange } =
    useViewModel(props);

  return (
    <div>
      {loading ? (
        <div className="mt-[8px] flex items-center gap-[8px] text-text-secondary">
          <SVGs.Spinner className="w-[16px] h-[16px] animate-spin" />
          <Text level={5} className="text-text-secondary">
            {props.t("booking.loadingTimeSlots")}
          </Text>
        </div>
      ) : (
        <ChipSelector
          required
          value={value}
          options={options}
          onChange={onChange}
          errorMessage={errorMessage}
          label={`${props.t("booking.availableTimeSlots")}${props.timeZone ? ` (${props.timeZone} time)` : ""}`}
        />
      )}
    </div>
  );
});
